import { RefCallback, useCallback, useRef } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';

import { setReturnTarget } from './drawerFocusReturn';
import { HOTKEY_SCOPES } from './scopes';

/**
 * Find the TOP drawer's main tablist + active tab in the live DOM (NOT React
 * state), mirroring the synchronous ArrowRight selection (topmost
 * `[data-testid="drawer-content"]` → first `[role="tablist"]` → active tab,
 * falling back to the first tab). Returns `null` if either is absent yet.
 */
function findTopDrawerActiveTab(): HTMLElement | null {
  const drawers = document.querySelectorAll<HTMLElement>(
    '[data-testid="drawer-content"]'
  );
  const top = drawers[drawers.length - 1];
  if (!top) return null;
  const tablist = top.querySelector('[role="tablist"]');
  return (
    tablist?.querySelector<HTMLElement>('[role="tab"][data-state="active"]') ??
    tablist?.querySelector<HTMLElement>('[role="tab"]') ??
    null
  );
}

/**
 * Deferred-enter (ENG-43, drawer-not-settled race): when `→` is pressed during
 * the drawer's initial-load spinner phase the `[role="tablist"]` isn't mounted
 * yet, so the synchronous selection finds nothing. Instead of dropping the key,
 * poll the live DOM via requestAnimationFrame until the tablist + active tab
 * appear, then focus the tab and arm the return target — exactly as the
 * synchronous path would have. Bounded to ~1 s so a drawer that never grows
 * tabs (page-level/standalone) can't leak an unbounded rAF loop.
 *
 * `Date.now()` (not `performance.now()`) drives the cap so the bound advances
 * under Vitest fake timers (`vi.runAllTimers()` flushes the rAF chain AND moves
 * the mocked clock), guaranteeing the loop terminates in tests.
 */
function deferEnterTopDrawerTab(returnEl: HTMLElement | null): () => void {
  const start = Date.now();
  let rafId = 0;
  const tick = () => {
    const activeTab = findTopDrawerActiveTab();
    if (activeTab) {
      setReturnTarget(returnEl);
      activeTab.focus();
      return;
    }
    if (Date.now() - start < 1000) rafId = requestAnimationFrame(tick);
  };
  rafId = requestAnimationFrame(tick);
  return () => cancelAnimationFrame(rafId);
}

export interface TableArrowNavOptions {
  /** Bind only when there are rows to navigate (e.g. `groupedData.length > 0`). */
  enabled: boolean;
  /** Number of navigable rows (`groupedData.length`). */
  itemCount: number;
  /** Index of the currently-inspected row, or `null` when none is inspected. */
  inspecting: number | null;
  /**
   * Move to `nextIndex`. The caller does the work (scrollToIndex +
   * setInspecting + onRowClick) so this hook owns ONLY the index math + binding.
   * `direction` tells the caller which way to skip past colgroup header rows
   * ('up' steps backward, 'down' steps forward) so a header at the wrap boundary
   * doesn't bounce navigation the wrong way.
   */
  onNavigate: (nextIndex: number, direction: 'up' | 'down') => void;
}

/**
 * Focus-scoped ArrowUp/ArrowDown row navigation for the shared data tables
 * (`Table` / `CanonicalTable`).
 *
 * Returns a `RefCallback` to attach to the table's scroll container. Because
 * react-hotkeys-hook's `useHotkeys` returns an element ref, the binding fires
 * ONLY while focus is inside that element. This is the deliberate replacement
 * for the old global keyboard bus (`keyboardListeners` on `useGlobalState`),
 * whose plain Arrow handlers fired GLOBALLY — so on a multi-table page the
 * last-registered table always won, regardless of where the user was looking.
 *
 * BEHAVIOR CHANGE (intended — see ENG-43): the new model is focus-scoped, so
 * the user must click/focus a table before arrows navigate its rows. On a
 * single-table page the arrows no longer work the instant the page loads; they
 * work once the table (or one of its rows) has focus. This is the fix for the
 * multi-table "last-wins" bug. Focus is acquired on row-click by the caller.
 *
 * Index math is preserved verbatim from the old bus:
 *  - ArrowDown: wrap to 0 at the end, else `inspecting + 1` (0 when none set).
 *  - ArrowUp:   `max(0, inspecting - 1)`; NO-OP while `inspecting === null`
 *               (matches the old "ArrowUp only once a row is inspected").
 *
 * Scope is `tableNav` — arrow-nav is NOT globally suppressed (unlike action
 * shortcuts on the `global` scope). It relies SOLELY on the element-ref focus
 * gate for arbitration: the binding fires only while focus is inside this
 * table's scroll container, which already disambiguates "table behind a drawer"
 * (no focus → won't fire) from "table inside a drawer" (can hold focus → must
 * still navigate). Routing through `global` would have been both redundant with
 * the focus gate and harmful — `HotkeyScopeSync` disables `global` on any
 * drawer-open, which would kill arrow-nav for the shared `<Vulnerabilities>`
 * list rendered inside the asset drawer.
 */
export function useTableArrowNav<T extends HTMLElement>(
  opts: TableArrowNavOptions
): RefCallback<T | null> {
  const { enabled, itemCount, inspecting, onNavigate } = opts;

  // The hook captures its OWN scroll-container node so the ArrowRight branch can
  // record it as the focus-return target (Esc-from-drawer-tabs comes back here).
  const elRef = useRef<T | null>(null);
  const cancelDeferRef = useRef<(() => void) | null>(null);

  const hotkeyRef = useHotkeys<T>(
    ['up', 'down', 'right'],
    (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        // No row open ⇒ no drawer to enter; let the browser do its normal thing.
        if (inspecting === null) return;

        // Topmost drawer wins (DOM order = stack order, drawers/index.tsx); a
        // child drawer renders after its parent.
        const drawers = document.querySelectorAll<HTMLElement>(
          '[data-testid="drawer-content"]'
        );
        if (drawers.length === 0) return; // no drawer → no-op
        const topDrawer = drawers[drawers.length - 1];

        // The MAIN tablist is the FIRST [role="tablist"] in document order inside
        // the drawer. POE's nested tablist renders inside the Evidence tab panel,
        // which comes AFTER the main tablist in the DOM, so "first" reliably
        // selects the main tabs.
        // v1 boundary: `inspecting` is table-local and not reset on drawer close,
        // so if a table abnormally retains focus across an UNRELATED drawer open,
        // `→` targets the topmost drawer's tabs (focus-gated, non-crashing) —
        // accepted v1 boundary, same class as nested-table `→`.
        const tablist = topDrawer.querySelector('[role="tablist"]');
        const activeTab =
          tablist?.querySelector<HTMLElement>(
            '[role="tab"][data-state="active"]'
          ) ?? tablist?.querySelector<HTMLElement>('[role="tab"]');

        if (!activeTab) {
          // Drawer-not-settled race: the drawer IS open but its tablist (or
          // active tab) hasn't mounted yet (initial-load spinner phase). We ARE
          // handling `→` here — suppress native scroll and defer the focus via a
          // bounded rAF poll that re-checks the TOP drawer once the tablist
          // mounts, then focuses it + arms the return target (mirrors the
          // synchronous path below).
          cancelDeferRef.current?.();
          cancelDeferRef.current = deferEnterTopDrawerTab(elRef.current);
          event.preventDefault();
          return;
        }

        // Common case: tablist + active tab already present → enter synchronously.
        setReturnTarget(elRef.current);
        activeTab.focus();
        event.preventDefault();
        return;
      }

      if (event.key === 'ArrowUp') {
        // Old behavior: ArrowUp does nothing until a row is inspected.
        if (inspecting === null) return;
        onNavigate(Math.max(0, inspecting - 1), 'up');
        return;
      }

      // ArrowDown (the only other bound key): wrap to top at the end.
      const nextIndex =
        inspecting === null || inspecting + 1 >= itemCount ? 0 : inspecting + 1;
      onNavigate(nextIndex, 'down');
    },
    {
      enabled,
      scopes: [HOTKEY_SCOPES.tableNav],
      enableOnFormTags: false,
      // ArrowUp/ArrowDown always do row-nav, so blanket-suppress their native
      // scroll (unchanged). ArrowRight, however, has no-op early-returns (no row
      // inspected / no drawer / no tablist) where it must NOT swallow the
      // browser's native horizontal scroll of the focused table container; on
      // its SUCCESS path the handler calls event.preventDefault() itself. So
      // ArrowRight self-manages — the option-level predicate returns false for it
      // (v5 invokes `preventDefault(event, hotkey)` BEFORE the handler body).
      preventDefault: (event: KeyboardEvent) => event.key !== 'ArrowRight',
    },
    // react-hotkeys-hook freezes the callback via useCallback until a listed
    // dep changes. `onNavigate` MUST be listed: it closes over rawData /
    // virtualizer / setInspecting (see Table.tsx onNavigate), so on a
    // same-count change (sort, row-count-preserving filter, refetch) a frozen
    // closure would call a stale onNavigate and open the wrong/old row.
    // itemCount / inspecting / enabled feed the index math + binding gate.
    [itemCount, inspecting, enabled, onNavigate]
  );

  // Merge the RefCallback from useHotkeys with our own elRef so we can both gate
  // the hotkey binding (hotkeyRef) and read the scroll-container node directly
  // (elRef) when recording the focus-return target. useCallback keeps the
  // RefCallback identity stable so React doesn't detach/reattach the ref.
  // (Mirrors the setWrapperRef merge pattern in Tabs.tsx.)
  return useCallback<RefCallback<T | null>>(
    (node: T | null) => {
      hotkeyRef(node);
      elRef.current = node;
    },
    [hotkeyRef]
  );
}
